/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
	ContainerCleaningPayload,
	PAContainerCleaning,
} from "../mediaCleaning.types";
import { postAutoclaveServices } from "../../../../../service/postAutoclaveServices";

interface Props {
	passed: number;
	failed: number;
	toggleConfirmationModal: () => void;
	showToast: (message: string, toastType: "success" | "error") => void;
	containers: PAContainerCleaning[];
	cleaningAgentId: string;
	cleaningMethod: string;
	handleSuccess: () => void;
	mediaBatchId: string;
}

const PACleaningConfirmationModal: React.FC<Props> = ({
	passed,
	failed,
	containers,
	cleaningAgentId,
	cleaningMethod,
	showToast,
	toggleConfirmationModal,
	handleSuccess,
	mediaBatchId
}) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false); // 👈 loading state

	const payload: ContainerCleaningPayload = {
		media_batch_id: mediaBatchId,
		cleaning_agent_id: cleaningAgentId,
		cleaning_method: cleaningMethod,
		containers: containers.map((cont) => ({
			container_id: Number(cont.container_id),
			visual_cleanliness: cont.visual_cleanliness,
			remarks: cont.remarks ?? "",
		})),
	};

	const handlePost = async () => {
		setIsLoading(true);
		try {
			const response =
				await postAutoclaveServices.postContainerCleaning(payload);

			if (response.statusCode !== 200) {
				showToast(response.message || "Something went wrong", "error");
			} else {
				handleSuccess();
			}
		} catch (error: any) {
			console.log(error)
			showToast(error?.response?.data?.message || "An unexpected error occurred", "error");
		} finally {
			setIsLoading(false);     // 👈 always reset loading
			toggleConfirmationModal(); // 👈 always close modal after attempt
		}
	};

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 animate-in fade-in duration-300">
			<div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-300">
				<div className="p-12 text-center">
					<div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-emerald-100">
						<span className="material-symbols-outlined text-emerald-500 text-5xl">
							verified
						</span>
					</div>
					<h3 className="text-2xl font-black text-[#1E293B] mb-4 tracking-tight">
						{t("postAutoclaveProcess.mediaCleaning.completeAssessment")}
					</h3>
					<div className="bg-slate-50 rounded-3xl p-8 mb-8 space-y-6">
						<div className="grid grid-cols-2 gap-4">
							<div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
								<p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">
									{t("postAutoclaveProcess.mediaCleaning.passed")}
								</p>
								<p className="text-2xl font-black text-emerald-600">
									{passed}
								</p>
							</div>
							<div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
								<p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">
									{t("postAutoclaveProcess.mediaCleaning.failed")}
								</p>
								<p className="text-2xl font-black text-red-600">
									{failed}
								</p>
							</div>
						</div>
						<div className="space-y-3 text-left">
							<div className="flex items-center gap-3 text-slate-600">
								<span className="material-symbols-outlined text-emerald-500 text-lg">
									check_circle
								</span>
								<p className="text-xs font-bold">
									<span className="text-emerald-600">
										{passed}{" "}
										{t("postAutoclaveProcess.mediaCleaning.plates")}
									</span>{" "}
									{t("postAutoclaveProcess.mediaCleaning.passedPlatesCleaningWould")}
								</p>
							</div>
							<div className="flex items-center gap-3 text-slate-600">
								<span className="material-symbols-outlined text-red-500 text-lg">
									cancel
								</span>
								<p className="text-xs font-bold">
									<span className="text-red-600">
										{failed}{" "}
										{t("postAutoclaveProcess.mediaCleaning.plates")}
									</span>{" "}
									{t("postAutoclaveProcess.mediaCleaning.failedAndWillBESent")}
								</p>
							</div>
						</div>
						<div className="h-px bg-slate-200 w-full"></div>
						<p className="text-sm font-black text-[#1E293B]">
							{t("postAutoclaveProcess.mediaCleaning.doYouWantToContinue")}
						</p>
					</div>

					<div className="flex gap-4">
						<button
							onClick={toggleConfirmationModal}
							disabled={isLoading} // 👈 prevent closing mid-request
							className="flex-1 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{t("postAutoclaveProcess.mediaCleaning.cancel")}
						</button>
						<button
							onClick={handlePost}
							disabled={isLoading} // 👈 prevent double submission
							className="flex-1 px-8 py-4 bg-[#2D3E50] text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
						>
							{isLoading ? (
								<>
									{t("postAutoclaveProcess.mediaCleaning.submitting")}
								</>
							) : (
								t("postAutoclaveProcess.mediaCleaning.continue")
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PACleaningConfirmationModal;
